import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiCalendar } from 'react-icons/fi';
import { VisitScheduleUpdatedNotificationType } from '../../types/notifications';
import { LIST_ITEM_ANIMATIONS } from '../consts/motionAnimations';

type ScheduleUpdatedWidgetNotificationCardProps = {
  notificationDetails: VisitScheduleUpdatedNotificationType;
  onClick: (id: number) => void;
};

/**
 * The widget's line for a planning-session summary.
 *
 * The widget reads the same stored metadata as the full list, so it can show the change count
 * — but only that: the breakdown belongs on the list card, where there is room for it.
 */
const ScheduleUpdatedWidgetNotificationCard = ({
  notificationDetails,
  onClick,
}: ScheduleUpdatedWidgetNotificationCardProps) => {
  const { t } = useTranslation();
  const timeAgo = (iso: string) => dayjs(iso).fromNow();
  const changeCount = notificationDetails.metadata?.[0]?.changeCount;

  return (
    <motion.li key={notificationDetails.notificationId} className="overflow-hidden" {...LIST_ITEM_ANIMATIONS}>
      <div className="group flex flex-col px-4 py-3 hover:bg-slate-50 cursor-pointer">
        <div
          onClick={() => onClick(notificationDetails.notificationId)}
          className="flex w-full items-start gap-3 text-left"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
            <FiCalendar fontSize={16} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between">
              <p className="truncate text-sm font-semibold text-slate-800">{notificationDetails.title}</p>
              <time className="ml-2 flex-shrink-0 text-xs text-slate-500">
                {timeAgo(notificationDetails.createdAt)}
              </time>
            </div>
            <p className="mt-1 text-xs text-slate-600 line-clamp-2">{notificationDetails.message}</p>

            <div className="mt-2 flex items-center gap-2">
              {typeof changeCount === 'number' && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                  {t('notifications-schedule-updated-change-count', { count: changeCount })}
                </span>
              )}
              {!notificationDetails.isRead && (
                <div className="h-2 w-2 rounded-full bg-blue-500" aria-label={t('notifications-new-badge')} />
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.li>
  );
};

export default ScheduleUpdatedWidgetNotificationCard;
